'use client';
import {
  Button,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormInput,
} from '@baseball-near-you/ui';
import { FormReferralSourceDropdown } from '@bb/components/ReferralSourceDropdown';
import { useUserProfile } from '@bb/lib/hooks/useUserProfile';
import { useUpdateUserMutation } from '@bb/lib/mutations/useUpdateUserMutation';
import { CheckCircle, ChevronLeft } from 'lucide-react';
import posthog from 'posthog-js';
import { useState } from 'react';
import { z } from 'zod';
import { Step } from './TeamSelectionModal';

export function TeamOnboardingStep({
  setStep,
  selectedTeam,
  onSuccess,
  setOpen,
}: {
  setStep: (step: Step) => void;
  selectedTeam: { name: string; slug: string } | null;
  setOpen: (open: boolean) => void;
  onSuccess?: () => Promise<void>;
}) {
  const { updateUser, updatingUser } = useUpdateUserMutation({
    showToast: false,
  });
  const { userProfile } = useUserProfile();
  const [loading, setLoading] = useState(false);

  return (
    <Form
      onSubmit={async (data) => {
        setLoading(true);
        await updateUser({
          id: userProfile?.id!,
          role: data.role,
          referralSource: data.referralSource,
        });
        posthog.capture('successful_claim_team_during_onboarding', {
          user_id: userProfile?.id,
        });
        await onSuccess?.();
        setOpen(false);
        setLoading(false);
      }}
      schema={z.object({
        role: z.string().min(1, 'Required.'),
        referralSource: z.string().min(1, 'Required.'),
      })}
      initialValues={{
        role: userProfile?.role,
        referralSource: userProfile?.referralSource,
      }}
    >
      <Button
        variant="ghost"
        className="absolute top-4 left-4 h-min rounded-sm p-0! opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
        onClick={() =>
          setStep(selectedTeam ? Step.Background : Step.TeamSelection)
        }
        type="button"
      >
        <ChevronLeft size={20} />
      </Button>
      <DialogHeader>
        <DialogTitle className="mt-8 mb-2">Just a few more details</DialogTitle>
      </DialogHeader>
      <div className="flex flex-col space-y-5">
        <p className="text-left">
          We need a few more pieces of information to allow you to begin
          updating your team!
        </p>

        <FormInput
          name="role"
          label="Your role with the team"
          placeholder="Head coach"
        />
        <FormReferralSourceDropdown name="referralSource" />
      </div>
      <DialogFooter className="mt-8">
        <div className="flex w-full flex-col border-t">
          {!!selectedTeam && !selectedTeam.slug && (
            <p className="mt-2 self-center truncate rounded-md border bg-gray-50 px-4 py-1 font-semibold text-lg">
              Selected team - {selectedTeam.name}
            </p>
          )}
          <Button
            className="mt-4 w-min self-end whitespace-nowrap bg-green-600"
            type="submit"
            loading={loading}
          >
            <CheckCircle size={14} className="mr-2" />
            Finish onboarding
          </Button>
        </div>
      </DialogFooter>
    </Form>
  );
}
